import { Injectable, inject, signal } from '@angular/core';
import type { Account } from '@mwe/models';
import { LocalStorageService, SessionStorage, SessionStorageService } from '@mwe/utils';
import Cookies from 'js-cookie';
import { EnvironmentService } from '../../environment/environment.service';
import { EnvironmentCustomerType } from '@mwe/environment';

export const accountSessionIdKey = 'account_sessionId';
const accountKey = 'account_currentaccount';
const islogwiensyncedKey = 'account_islogwiensynced';
const isBskwUserKey = 'account_isBskw';

@Injectable()
export class AccountStateService {
  private readonly storage = inject(LocalStorageService);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly environmentService = inject(EnvironmentService);

  private readonly isCommunity = signal<boolean>(false);

  constructor() {
    this.isCommunity.set(this.environmentService.getPortalCustomerType() === EnvironmentCustomerType.COMMUNITY);
  }

  @SessionStorage(islogwiensyncedKey)
  private _isLogwienSynced: boolean;

  @SessionStorage(accountSessionIdKey)
  private _uuid: string;

  // this service should be private, unfortunately there is no official angular-way to hide it
  // use AccountLogic to access data

  get uuid(): string {
    return this._uuid;
  }

  set uuid(newValue: string) {
    if (!newValue) {
      this.sessionStorage.clear(accountSessionIdKey);
      return;
    }
    this._uuid = newValue;
  }

  @SessionStorage(isBskwUserKey)
  private _isBskwUser: boolean;

  get isBskwUser(): boolean {
    return this._isBskwUser;
  }

  set isBskwUser(newValue: boolean) {
    if (newValue == undefined) {
      this.storage.clear(isBskwUserKey);
      return;
    }
    this._isBskwUser = newValue;
  }

  get account(): Account {
    const account = Cookies.get(accountKey);
    if (account) {
      return JSON.parse(account);
    }
    return null;
  }

  set account(newValue: Account) {
    if (!newValue) {
      Cookies.remove(accountKey, { path: '/privat' });
      Cookies.remove(accountKey, { path: '/business' });
      if (this.isCommunity()) Cookies.remove(accountKey, { path: '/' });
      return;
    }

    Cookies.set(accountKey, JSON.stringify(newValue), { secure: false, path: '/privat' });
    Cookies.set(accountKey, JSON.stringify(newValue), { secure: false, path: '/business' });
    if (this.isCommunity()) Cookies.set(accountKey, JSON.stringify(newValue), { secure: false, path: '/' });
  }

  clearBeforeCollaboration(): void {
    Cookies.remove(accountKey, { path: '/privat' });
    Cookies.remove(accountKey, { path: '/business' });
    if (this.isCommunity()) Cookies.remove(accountKey, { path: '/' });
  }

  isImpersonate(): boolean {
    return !!this.account?.impersonator;
  }
}
